// Cookie consent state, per category (GDPR + ePrivacy / Danish cookie order):
//   - necessary:   always on (cart, checkout, wishlist, this consent record).
//   - preferences: remembers recently viewed products and recent searches.
//   - statistics:  Google Analytics 4 + the anonymous product-view counter.
//   - marketing:   Google ad signals + Shopify Custom Pixel forwarding (ad pixels).
//
// Stored in localStorage under "fvl_consent_v2" as JSON with a timestamp.
// Until a decision is made, nothing but necessary storage may be used.
// Consent expires after 12 months, after which the banner asks again.

export type ConsentCategory = "preferences" | "statistics" | "marketing";

export type ConsentChoices = Record<ConsentCategory, boolean>;

export type ConsentState = ConsentChoices & {
  /** Schema version of the stored record. */
  v: 2;
  /** When the visitor made this choice (ms since epoch). */
  ts: number;
};

export type ConsentValue = ConsentState | null;

export const CONSENT_CATEGORIES: ConsentCategory[] = ["preferences", "statistics", "marketing"];

const KEY = "fvl_consent_v2";
const LEGACY_KEY = "fvl_consent_v1";
const EVT = "fvl:consent-change";
const OPEN_EVT = "fvl:consent-open";
const MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

const ALL_ON: ConsentChoices = { preferences: true, statistics: true, marketing: true };
const ALL_OFF: ConsentChoices = { preferences: false, statistics: false, marketing: false };

function isState(v: unknown): v is ConsentState {
  if (!v || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  return (
    o.v === 2 &&
    typeof o.ts === "number" &&
    CONSENT_CATEGORIES.every((c) => typeof o[c] === "boolean")
  );
}

function write(choices: ConsentChoices, ts = Date.now()): ConsentState {
  const state: ConsentState = { v: 2, ts, ...choices };
  try {
    window.localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
  return state;
}

export function getConsent(): ConsentValue {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (isState(parsed) && Date.now() - parsed.ts < MAX_AGE_MS) return parsed;
      return null;
    }
    // Migrate the old all-or-nothing record. A previous "accept" covered
    // analytics + pixel forwarding; a "reject" covered everything optional.
    const legacy = window.localStorage.getItem(LEGACY_KEY);
    if (legacy === "accepted" || legacy === "rejected") {
      window.localStorage.removeItem(LEGACY_KEY);
      return write(legacy === "accepted" ? ALL_ON : ALL_OFF);
    }
  } catch {
    // ignore
  }
  return null;
}

export function hasConsent(category: ConsentCategory): boolean {
  return getConsent()?.[category] === true;
}

export function setConsent(choices: ConsentChoices) {
  if (typeof window === "undefined") return;
  const state = write(choices);
  if (!choices.preferences) clearPreferenceStorage();
  if (!choices.statistics) clearStatisticsStorage();
  window.dispatchEvent(new CustomEvent<ConsentValue>(EVT, { detail: state }));
}

export function acceptAll() {
  setConsent(ALL_ON);
}

export function rejectAll() {
  setConsent(ALL_OFF);
}

export function clearConsent() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
  window.dispatchEvent(new CustomEvent<ConsentValue>(EVT, { detail: null }));
}

export function onConsentChange(cb: (v: ConsentValue) => void) {
  if (typeof window === "undefined") return () => {};
  const handler = (e: Event) => cb((e as CustomEvent<ConsentValue>).detail);
  window.addEventListener(EVT, handler);
  return () => window.removeEventListener(EVT, handler);
}

// ---------- Re-opening the settings (withdrawing consent) ----------
//
// The banner is lazy-mounted after idle, so a request that arrives before it
// mounts is remembered and picked up via `consumeOpenRequest()`.

let pendingOpen = false;

export function openConsentSettings() {
  if (typeof window === "undefined") return;
  pendingOpen = true;
  window.dispatchEvent(new Event(OPEN_EVT));
}

export function consumeOpenRequest(): boolean {
  const was = pendingOpen;
  pendingOpen = false;
  return was;
}

export function onConsentOpen(cb: () => void) {
  if (typeof window === "undefined") return () => {};
  const handler = () => {
    pendingOpen = false;
    cb();
  };
  window.addEventListener(OPEN_EVT, handler);
  return () => window.removeEventListener(OPEN_EVT, handler);
}

// ---------- Cleanup when consent is withdrawn ----------

function removeKeys(keys: string[]) {
  for (const k of keys) {
    try {
      window.localStorage.removeItem(k);
      window.sessionStorage.removeItem(k);
    } catch {
      // ignore
    }
  }
}

function clearPreferenceStorage() {
  removeKeys(["fl:recent:v1", "fl.search.recent"]);
}

function clearStatisticsStorage() {
  removeKeys(["fl:vid:v1", "fl:viewed:v1"]);
  // Expire Google Analytics cookies (_ga, _ga_<id>, _gid) on this host and
  // the parent domain, since GA sets them on the top-level domain.
  const host = window.location.hostname;
  const parts = host.split(".");
  const domains = ["", host, parts.length > 1 ? "." + parts.slice(-2).join(".") : ""];
  for (const pair of document.cookie.split(";")) {
    const name = pair.split("=")[0]?.trim();
    if (!name || !/^_ga($|_)|^_gid$|^_gat/.test(name)) continue;
    for (const d of domains) {
      document.cookie = `${name}=; Max-Age=0; path=/${d ? `; domain=${d}` : ""}`;
    }
  }
}
